import { useMemo } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { ScreenClassProvider } from '@vp/swan'
import { useLogger, useStyles, useUserContext } from '@vp/ubik-context'

import { stylesheet } from './FragmentRoot.scss'
import { configureStore } from '~/app/store/configureStore'
import { QueryProvider } from '~/app/features/FragmentRoot/contexts/QueryProvider'
import { IntlContext } from '~/app/features/FragmentRoot/contexts/IntlContext'
import { AnalyticsContext } from '~/app/features/FragmentRoot/contexts/gallery/AnalyticsContext'
import { Analytics } from 'shared/analytics'
import { FragmentRootProps } from '~/app/features/FragmentRoot/interface'
import { IS_BROWSER } from '~/app/constants'
import { IdentityProviderWrapper } from '~/app/features/FragmentRoot/contexts/identity'
import { PricingContextProviderWrapper } from '~/app/features/FragmentRoot/contexts/pricing/provider'
import { Gallery as GalleryRoot } from '~/app/features/Gallery/index'
import { PersonalizationWidgetProviderWrapper } from './contexts/personalization'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { FEATURE_GATES } from 'shared/featureGates/constants'

export const FragmentRoot = ({ initialReduxState, initialQueryState, translations, personalizationTranslations }: FragmentRootProps) => {
  const logger = useLogger()
  const userContext = useUserContext()

  useStyles(stylesheet)

  if (IS_BROWSER) {
    logger.info('Rendering fragment client: ', initialReduxState.config, userContext)
  }

  const localizations = useMemo(() => ({ messages: translations }), [translations])
  const analytics = useMemo(() => new Analytics(logger), [logger])

  const isNewQVEnabled = useFeatureGate(FEATURE_GATES.GALLERY_QUICK_VIEW_MODAL_ENABLED)

  const store = configureStore(initialReduxState, analytics, isNewQVEnabled)

  return (
    <ReduxProvider store={store}>
      <IntlContext.Provider value={localizations}>
        <AnalyticsContext.Provider value={analytics}>
          <QueryProvider state={initialQueryState}>
            <ScreenClassProvider>
              <IdentityProviderWrapper>
                <PersonalizationWidgetProviderWrapper localizations={personalizationTranslations}>
                  <PricingContextProviderWrapper>
                    <GalleryRoot />
                  </PricingContextProviderWrapper>
                </PersonalizationWidgetProviderWrapper>
              </IdentityProviderWrapper>
            </ScreenClassProvider>
          </QueryProvider>
        </AnalyticsContext.Provider>
      </IntlContext.Provider>
    </ReduxProvider>
  )
}

FragmentRoot.displayName = 'FragmentRoot'
